/**
 * This service will handle the logic for creating the interactive site plan markers to place on top of
 * the site plans in the Search Results Page.
 */

import { catchError, mergeMap, of } from "rxjs";
import getCommunityInteractiveSitePlans$ from "../apis/getCommunityInteractiveSitePlans";
import { zoomLevelLayersLookup } from "../constants/sitePlansConstants";
import SitePlanLayer from "../enums/sitePlanLayer";
import LotStatus from "../enums/lotStatus";

/**
 * assignLotPathId: there are Interactive Site Plans that don't have the lot polygons identified.
 * This function is in charge of assigning IDs based on a pixel in the #lots group.
 * @param {int} communityId the site plan identifier 
 */
export const assignLotPathId = (sitePlanId) => {
    const sitePlanSvg = document.querySelector(`[data-site-plan-id="${sitePlanId}"]`);
    const polygonsContainer = document.querySelector(`[data-polygon-container-id="${sitePlanId}"]`);
    const lotPaths = polygonsContainer?.querySelectorAll('#lot_polygons path:not([id])');
    const lotsSvg = sitePlanSvg?.querySelector(`#${SitePlanLayer.Lots.id}`)?.closest('svg');
    const lotPixels = lotsSvg?.querySelectorAll('.anchor');

    if (lotPaths.length === 0 || !lotsSvg) {
        lotsSvg?.remove();
        return;
    }

    lotPixels.forEach((pixel) => {
        pixel.style.display = "block";
        const pixelBounds = pixel.getBoundingClientRect();       
        const lotPath = Array.from(lotPaths).find((path) => {
            const pathBounds = path.getBoundingClientRect();
            const shrinkFactor = 0.5;
            const shrunkPathBounds = {
                left: pathBounds.left + pathBounds.width * (1 - shrinkFactor) / 2,
                right: pathBounds.right - pathBounds.width * (1 - shrinkFactor) / 2,
                top: pathBounds.top + pathBounds.height * (1 - shrinkFactor) / 2,
                bottom: pathBounds.bottom - pathBounds.height * (1 - shrinkFactor) / 2
            };

            return !(
                shrunkPathBounds.right < pixelBounds.left ||
                shrunkPathBounds.left > pixelBounds.right ||
                shrunkPathBounds.bottom < pixelBounds.top ||
                shrunkPathBounds.top > pixelBounds.bottom);
            });

        const lotId = pixel.getAttribute('id').replace('a', 'l');
        lotPath?.setAttribute('id', lotId);
        pixel.style.display = "none";
    });

    lotsSvg.remove();
};

/**
 * createMarkerHtmlElement function is in charge of building the HTML node of a single interactive marker.
 * @param {Object} marker containing information to build a marker and render it on screen.
 * @param {HTML} container to hold the created markers.
 * @returns the HTML of the created marker.
 */
const createMarkerHtmlElement = (marker, container) => {
    const newMarkerElement = document.createElement('div');
    newMarkerElement.innerHTML =
    `<div class="interactive-lot_marker" data-interactive-marker-id="${marker.Id}" hidden>
          ${marker.Label}
      </div>`;

    container.appendChild(newMarkerElement);
    return newMarkerElement;
};

/**
 * addInteractiveSitePlan function will be called whenever a new set of interactive
 * markers is added as a layer on top of the map in the Search Results Page.
 * @param {Array} markers to be rendered on top of a site plan.
 * @param {HTML} container to render the interactive markers in.
 * @param {int} communityId to be emited as a custom event when the markers are created. 
 */
export const addInteractiveSitePlan = ({ markers, container, communityId }) => {
    markers?.filter((marker) =>
        marker.Status === LotStatus.Sold.toString() ||
        !!marker.HousingUnitId ||
        marker.HousePlanIds.length > 0)
        .forEach((marker) => createMarkerHtmlElement(marker, container, communityId));
};

/**
 * drawInteractiveSitePlan function draws a set of markers.
 * @param {Array} markers to draw on the map.
 * @param {HTML} container to hold the created markers.
 * @param {int} scale pixels to consider when drawing the map.
 */
export const drawInteractiveSitePlan = ({ markers, container, scale, sitePlanId, communityId }) => {
    const smallSizeZoom = 18;
    const mediumSizeZoom = 19;

    document.body.dispatchEvent(
        new CustomEvent('interactiveMarkersReady', {
          detail: {
            communityId,
            sitePlanId,
          },
        })
      );

    markers.forEach((marker) => {
        const markerElement = container.querySelector(`[data-interactive-marker-id="${marker.Id}"]`);

        if (!markerElement) {
            return;
        }

        if (!marker.isVisible) {
            markerElement.style.display = 'none';
            return;
        }

        markerElement.className = 'interactive-lot_marker';
        markerElement.style.left = `${marker.center.x - scale / 2}px`;
        markerElement.style.top = `${marker.center.y - scale / 2}px`;
        markerElement.style.display = 'flex';

        if (scale <= smallSizeZoom) {
            markerElement.classList.add('small-markers');
        } else if (scale <= mediumSizeZoom) {
            markerElement.classList.add('medium-markers');
        } else {
            markerElement.classList.add('big-markers');
        }
    });
}

/**
 * removeInteractiveSitePlan function removes the container holding a set of interactive markers.
 * @param {HTML} container holding the interactive map markers that needs to be removed. 
 */
export const removeInteractiveSitePlan = ({container}) => {
    container.remove();
}

/**
 * getCommunityInteractiveSitePlanInformation$ will emit a stream of data retrieved from server side
 * related to interactive site plan markers to show on top of a community site plan.
 * @param {Array} sitePlans array to retrieve their interactive site plan markers from.
 * @returns an stream of data containing interactive markers for a set of community site plans.
 */
export const getCommunityInteractiveSitePlanInformation$ = (communityIds) => {
    const payload = {
        communityIds: [...new Set(communityIds)].join(','),
    };

    return getCommunityInteractiveSitePlans$(payload).pipe(
        mergeMap((interactiveSitePlans) => of(interactiveSitePlans)),
        catchError(() => of([])),
    );
};

/**
 * handleInteractiveMapLayersVisibility shows and hides layers of a community interactive map
 * based on the current zoom level.
 * @param {int} zoomLevel of the current map.
 * @param {HTMLElement} sitePlan representation in the map.
 */
export const handleInteractiveMapLayersVisibility = (zoomLevel, sitePlan) => {
    const layersToShow = Array.from(zoomLevelLayersLookup.entries())
        .filter(([key]) => key <= zoomLevel)
        .flatMap(([, layers]) => layers);

    const layersToHide = Array.from(zoomLevelLayersLookup.entries())
        .filter(([key]) => key > zoomLevel)
        .flatMap(([, layers]) => layers);

    layersToShow.forEach((layer) => {
        const path = sitePlan.querySelector(`#${layer.id}`);
        path?.removeAttribute('hidden');
        path?.removeAttribute('fill');
        path?.removeAttribute('stroke');
    });

    layersToHide.forEach((layer) => {
        const path = sitePlan.querySelector(`#${layer.id}`);
        path?.setAttribute('hidden', '');
        path?.removeAttribute('fill');
        path?.removeAttribute('stroke');
    });
};